import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Phone, Clock, ArrowUpDown } from "lucide-react";
import Card from "src/components/Card";
import ReactSelect from "src/base-components/ReactSelect";
import DatePicker from "src/base-components/DatePicker";
import type { DateValueType } from "react-tailwindcss-datepicker";
import { CallLogsState, userProps } from "src/interfaces";
import { RootState } from "src/stores";
import { fetchAllUsers, fetchLocationBaseAgents } from "src/stores/user";
import Lucide from "src/base-components/Lucide";
import Chart from "chart.js/auto";
import {
  fetchCallStatsAnalytics,
  fetchCallLogsLocationsService,
  fetchCallLogsAgentsService,
} from "src/services/calllogs.services";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CallLogsTabs from "src/components/CallLogsTabs";

export interface SelectOption {
  label: string;
  value: string;
}

export interface FiltersState {
  date: DateValueType;
  orderBy: string;
  agentIds?: string[];
  locationIds?: string[];
}

const FullPageSkeleton = () => (
  <div className="fixed z-[9999] bg-white container mx-auto overflow-hidden p-4 rounded-xl">
    {/* Header */}
    <div className="flex justify-between items-center mb-6">
      <div className="space-y-2">
        <Skeleton height={20} width={220} />
        <Skeleton height={12} width={120} />
      </div>
      <Skeleton height={36} width={120} borderRadius={12} />
    </div>

    {/* Filters */}
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 shadow-sm">
      <div className="flex flex-wrap gap-3">
        <Skeleton height={38} width={220} borderRadius={12} />
        <Skeleton height={38} width={170} borderRadius={12} />
        <Skeleton height={38} width={170} borderRadius={12} />
        <Skeleton height={38} width={170} borderRadius={12} />
        <Skeleton height={38} width={42} borderRadius={12} />
        <Skeleton height={38} width={80} borderRadius={12} />
      </div>
    </div>

    {/* Current Day Statistics */}
    <div className="mb-6">
      <Skeleton height={14} width={170} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
          >
            <Skeleton height={12} width={110} />
            <Skeleton height={28} width={70} className="mt-2" />
            <Skeleton height={12} width={140} className="mt-2" />
            <div className="mt-4">
              <Skeleton height={44} width={44} borderRadius={12} />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Agent Performance */}
    <div className="mb-6">
      <Skeleton height={14} width={150} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
          >
            <Skeleton height={12} width={120} />
            <Skeleton height={28} width={70} className="mt-2" />
            <Skeleton height={12} width={180} className="mt-2" />
            <div className="mt-4">
              <Skeleton height={44} width={44} borderRadius={12} />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Call Trends */}
    <div className="mb-6">
      <Skeleton height={14} width={110} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
          >
            <Skeleton height={12} width={160} />
            <Skeleton height={176} className="mt-3" borderRadius={12} />
          </div>
        ))}
      </div>
    </div>

    {/* Customer Insights */}
    <div>
      <Skeleton height={14} width={150} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
          >
            <Skeleton height={12} width={140} />
            <Skeleton count={5} className="mt-3" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ------------------------------ COMPONENT ------------------------------ */

const CallLogsAnalytics = ({
  getCalls,
  users,
  fetchData,
}: {
  getCalls: CallLogsState;
  users: userProps;
  fetchData: (payload?: any) => void;
}) => {
  const navigate = useNavigate();

  // Calculate default weekly date range (Monday to Sunday of current week)
  const getDefaultWeekRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const formatDate = (d: Date) => d.toISOString().split("T")[0];

    return {
      startDate: formatDate(monday),
      endDate: formatDate(sunday),
    };
  };

  const defaultWeek = getDefaultWeekRange();

  const initialFilters: FiltersState = {
    date: { startDate: defaultWeek.startDate, endDate: defaultWeek.endDate },
    orderBy: "desc",
    agentIds: [],
    locationIds: [],
  };

  const [filters, setFilters] = useState<FiltersState>(initialFilters);

  const updateFilter = <K extends keyof FiltersState>(
    name: K,
    value: FiltersState[K],
  ) => setFilters((prev) => ({ ...prev, [name]: value }));
  const [stats, setStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  // New state for locations and agents
  const [locations, setLocations] = useState<SelectOption[]>([]);
  const [agents, setAgents] = useState<SelectOption[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset call-logs page on filters
  useEffect(() => {}, [
    filters.date?.startDate,
    filters.date?.endDate,
    filters.orderBy,
    JSON.stringify(filters.agentIds || []),
    JSON.stringify(filters.locationIds || []),
  ]);

  const clearFilters = () => setFilters(initialFilters);

  const canEnableOrderBy = (f: FiltersState) => {
    const hasDate = f.date?.startDate !== null || f.date?.endDate !== null;
    const hasAgents = f.agentIds && f.agentIds.length > 0;
    const hasLocations = f.locationIds && f.locationIds.length > 0;
    return hasDate || hasAgents || hasLocations;
  };

  /* ===================== ANALYTICS API ===================== */

  // Fetch locations
  const fetchLocations = async () => {
    try {
      setLocationsLoading(true);
      const result = await fetchCallLogsLocationsService();

      // Try both result.data and result directly in case API structure varies
      const locationData = result.data || result || [];
      const locationOptions =
        locationData.map((location: any) => ({
          label: location.name || location.location_name || "Unknown Location",
          value: location.id || location.location_id || "",
        })) || [];

      console.log("Mapped location options:", locationOptions); // Debug log

      setLocations(locationOptions);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    } finally {
      setLocationsLoading(false);
    }
  };

  const fetchAgents = async (locationIds: string[]) => {
    const validLocationIds = locationIds.filter((id) => id && id !== "");

    if (validLocationIds.length === 0) {
      setAgents([]);
      return;
    }

    try {
      setAgentsLoading(true);
      const result = await fetchCallLogsAgentsService(validLocationIds);
      const agentsData = result.agents || result.data || [];
      const agentOptions =
        agentsData.map((agent: any) => ({
          label:
            `${agent.name} ${agent.last_name}`.trim() ||
            agent.name ||
            "Unknown Agent",
          value: agent.id?.toString() || "",
        })) || [];

      console.log("Mapped agent options:", agentOptions);

      setAgents(agentOptions);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
    } finally {
      setAgentsLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    const validLocationIds =
      filters.locationIds?.filter((id) => id && id !== "") || [];
    if (validLocationIds.length > 0) {
      fetchAgents(validLocationIds);
    } else {
      setAgents([]);
      updateFilter("agentIds", []);
    }
  }, [filters.locationIds]);

  const buildAnalyticsParams = () => ({
    start_date: filters.date?.startDate ?? null,
    end_date: filters.date?.endDate ?? null,
    agent_id:
      filters.agentIds
        ?.filter((id) => id !== "")
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id)) || [],
    location: filters.locationIds?.filter((id) => id !== "") || [],
  });

  const loadCallStatsAnalytics = async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);

      const params = buildAnalyticsParams();
      const res: any = await fetchCallStatsAnalytics(params);

      setStats(res?.data ?? res);
      setHasFetchedOnce(true);
    } catch (e: any) {
      setStats(null);
      setStatsError(e?.message || "Failed to load analytics");
      setHasFetchedOnce(true);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch analytics when filters/date changes
  useEffect(() => {
    const t = setTimeout(() => {
      loadCallStatsAnalytics();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.date?.startDate,
    filters.date?.endDate,
    JSON.stringify(filters.agentIds || []),
    JSON.stringify(filters.locationIds || []),
  ]);

  const safeStatsData = stats;

  // Helper function to safely get arrays
  const safeArray = (value: any): any[] => {
    return Array.isArray(value) ? value : [];
  };

  /* ===================== UI TEXT DATA (API) ===================== */

  const currentDayStats = {
    totalCalls: safeStatsData?.current_day_statistics?.total_calls ?? 0,
    totalCallsDate: safeStatsData?.current_day_statistics?.date_label ?? "-",
    avgDuration:
      safeStatsData?.current_day_statistics?.avg_duration?.formatted ?? "0:00",
    avgDurationSecondary:
      safeStatsData?.current_day_statistics?.avg_duration?.label ?? "",
    peakHour: safeStatsData?.current_day_statistics?.peak_hour?.label ?? "-",
    peakHourCalls:
      (safeStatsData?.current_day_statistics?.peak_hour?.call_count ?? 0) > 0
        ? `${safeStatsData.current_day_statistics.peak_hour.call_count} calls in peak`
        : "0 calls in peak",
    activeAgentsCount:
      safeStatsData?.current_day_statistics?.active_agents?.count ?? 0,
    activeAgentsLabel:
      safeStatsData?.current_day_statistics?.active_agents?.names_label ?? "",
  };

  // Helper function to truncate active agents list for display
  // const getTruncatedActiveAgents = (fullLabel: string) => {
  //   if (!fullLabel) return "";

  //   const agents = fullLabel.split(", ");
  //   if (agents.length <= 5) return fullLabel;

  //   const first5 = agents.slice(0, 5).join(", ");
  //   const remaining = agents.length - 5;
  //   return `${first5} + ${remaining} more`;
  // };

  // // Helper function to truncate calls handled agents list for display
  // const getTruncatedCallsHandledAgents = (fullLabel: string) => {
  //   if (!fullLabel) return "";

  //   const agents = fullLabel.split(", ");
  //   if (agents.length <= 5) return fullLabel;

  //   const first5 = agents.slice(0, 5).join(", ");
  //   const remaining = agents.length - 5;
  //   return `${first5} + ${remaining} more`;
  // };

  const truncateList = (fullLabel: string, limit: number = 5) => {
    if (!fullLabel) return "";

    // split by comma, dot separator, or new line
    const items = fullLabel
      .split(/,\s*|\s*·\s*|\n+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (items.length <= limit) return items.join(", ");

    const firstItems = items.slice(0, limit).join(", ");
    const remaining = items.length - limit;

    return `${firstItems} + ${remaining} more`;
  };

  // Active Agents
  const getTruncatedActiveAgents = (fullLabel: string) => {
    return truncateList(fullLabel, 2);
  };

  // Calls Handled Agents
  const getTruncatedCallsHandledAgents = (fullLabel: string) => {
    return truncateList(fullLabel, 2);
  };
  const agentPerformanceStats = {
    callsHandled: safeStatsData?.agent_performance?.calls_handled?.total ?? 0,
    callsHandledLabel:
      safeStatsData?.agent_performance?.calls_handled?.agent_label ?? "",
    avgTalkTime:
      safeStatsData?.agent_performance?.avg_talk_time?.formatted ?? "0:00",
    avgTalkTimeLabel:
      safeStatsData?.agent_performance?.avg_talk_time?.label ?? "",
    totalTalkTime:
      safeStatsData?.agent_performance?.total_talk_time?.formatted ?? "0:00",
    totalTalkTimeLabel:
      safeStatsData?.agent_performance?.total_talk_time?.label ?? "",
    talkLengthBreakdown:
      safeStatsData?.agent_performance?.call_type_breakdown?.label ??
      "0 / 0 / 0 Normal / Very Short / Long",
  };


  console.log(agentPerformanceStats , "agentPerformanceStats");
  
  /* ===================== CHARTS ===================== */

  const hourlyChartRef = useRef<HTMLCanvasElement | null>(null);
  const dailyChartRef = useRef<HTMLCanvasElement | null>(null);
  const queueChartRef = useRef<HTMLCanvasElement | null>(null);
  const ivrChartRef = useRef<HTMLCanvasElement | null>(null);
  const chartsRef = useRef<Record<string, any>>({});

  const hourlyLabels = safeArray(
    safeStatsData?.call_trends?.hourly_heatmap,
  ).map((h: any) => h.hour);
  const hourlyValues = safeArray(
    safeStatsData?.call_trends?.hourly_heatmap,
  ).map((h: any) => h.call_count);

  const dailyLabels = safeArray(
    safeStatsData?.call_trends?.daily_volume?.data,
  ).map((d: any) => d.label);
  const dailyValues = safeArray(
    safeStatsData?.call_trends?.daily_volume?.data,
  ).map((d: any) => d.call_count);

  const queueLabels = safeArray(
    safeStatsData?.call_trends?.queue_distribution,
  ).map((q: any) => q.queue);
  const queueValues = safeArray(
    safeStatsData?.call_trends?.queue_distribution,
  ).map((q: any) => q.call_count);

  const ivrLabels = safeArray(
    safeStatsData?.ivr_routing_health?.calls_per_ivr_menu,
  ).map((i: any) => i.ivr_menu);
  const ivrValues = safeArray(
    safeStatsData?.ivr_routing_health?.calls_per_ivr_menu,
  ).map((i: any) => i.call_count);

  const queueColors = [
    "#1E3A8A",
    "#DC2626",
    "#0EA5E9",
    "#22C55E",
    "#F59E0B",
    "#9CA3AF",
  ];
  const hourlyBarColors = hourlyValues.map((v: number) =>
    v >= 5 ? "#1E3A8A" : "#CBD5E1",
  );

  const customerInsights = safeStatsData?.customer_insights;
  const uniqueCustomers = safeArray(customerInsights?.unique_customers?.list);
  const highFrequencyNumbers = safeArray(
    customerInsights?.high_frequency_numbers?.list,
  );
  const querySummary = safeArray(customerInsights?.query_summary?.list);
  const mostUsedIvrInputs = safeArray(
    safeStatsData?.ivr_routing_health?.most_used_ivr_inputs,
  );

  useEffect(() => {
    if (!safeStatsData) return;
    if (hourlyChartRef.current) {
      chartsRef.current.hourly?.destroy();
      const ctx = hourlyChartRef.current.getContext("2d");
      if (ctx) {
        chartsRef.current.hourly = new Chart(ctx, {
          type: "bar",
          data: {
            labels: hourlyLabels,
            datasets: [
              {
                data: hourlyValues,
                backgroundColor: hourlyBarColors,
                borderRadius: 8,
                maxBarThickness: 28,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { enabled: true } },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: "#94A3B8", font: { size: 10 } },
              },
              y: {
                grid: { color: "#EEF2F7" },
                ticks: { color: "#94A3B8", font: { size: 10 }, precision: 0 },
              },
            },
          },
        });
      }
    }

    if (dailyChartRef.current) {
      chartsRef.current.daily?.destroy();
      const ctx = dailyChartRef.current.getContext("2d");
      if (ctx) {
        chartsRef.current.daily = new Chart(ctx, {
          type: "line",
          data: {
            labels: dailyLabels,
            datasets: [
              {
                data: dailyValues,
                borderColor: "#1E3A8A",
                backgroundColor: "rgba(30,58,138,0.08)",
                tension: 0.4,
                fill: true,
                pointRadius: 3,
                pointBackgroundColor: "#1E3A8A",
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: "#94A3B8", font: { size: 10 } },
              },
              y: {
                grid: { color: "#E5E7EB" },
                ticks: { color: "#94A3B8", font: { size: 10 }, precision: 0 },
              },
            },
          },
        });
      }
    }

    // Queue doughnut
    if (queueChartRef.current) {
      chartsRef.current.queue?.destroy();
      const ctx = queueChartRef.current.getContext("2d");
      if (ctx) {
        chartsRef.current.queue = new Chart(ctx, {
          type: "doughnut",
          data: {
            labels: queueLabels,
            datasets: [
              {
                data: queueValues,
                backgroundColor: queueColors,
                borderWidth: 0,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            cutout: "70%",
          },
        });
      }
    }

    // IVR horizontal bars
    if (ivrChartRef.current) {
      chartsRef.current.ivr?.destroy();
      const ctx = ivrChartRef.current.getContext("2d");
      if (ctx) {
        chartsRef.current.ivr = new Chart(ctx, {
          type: "bar",
          data: {
            labels: ivrLabels,
            datasets: [
              {
                data: ivrValues,
                backgroundColor: "#1E3A8A",
                borderRadius: 8,
                barThickness: 14,
              },
            ],
          },
          options: {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              x: {
                grid: { color: "#E5E7EB" },
                ticks: { color: "#94A3B8", font: { size: 10 }, precision: 0 },
              },
              y: {
                grid: { display: false },
                ticks: { color: "#64748B", font: { size: 10 } },
              },
            },
          },
        });
      }
    }

    return () => {
      chartsRef.current.hourly?.destroy();
      chartsRef.current.daily?.destroy();
      chartsRef.current.queue?.destroy();
      chartsRef.current.ivr?.destroy();
    };
  }, [safeStatsData]);

  const showFullSkeleton = statsLoading;

  useEffect(() => {
    if (showFullSkeleton) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.width = "100%";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [showFullSkeleton]);


  console.log(agentPerformanceStats.totalTalkTime    , "agentPerformanceStats.totalTalkTime  agentPerformanceStats.totalTalkTime  agentPerformanceStats.totalTalkTime  ");
  

  return (
    <div className="relative">
      {showFullSkeleton && <FullPageSkeleton />}

      <div className="px-2 md:px-0">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col md:h-10 gap-y-2 md:items-center md:flex-row justify-between">
            <div className="text-base font-semibold text-slate-900 -mb-8">
              Call Logs Analytics
            </div>

            <div className="flex items-center gap-3">
              {statsError && (
                <div className="text-xs text-red-600">{statsError}</div>
              )}
            </div>

            <CallLogsTabs className="mb-6" />
          </div>

          {/* Filters */}
          <Card className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex flex-wrap gap-3 items-start">
              <div className="relative min-w-[220px]">
                <DatePicker
                  value={filters.date}
                  onChange={(value) => updateFilter("date", value)}
                  classNames={{
                    input: () =>
                      "h-[38px] transition duration-200 ease-in-out w-full text-sm border-slate-300/100 shadow-sm !rounded-xl placeholder:text-slate-400/90 focus:ring-primary focus:ring-opacity-20 focus:border-none focus:ring-4",
                  }}
                  placeholder="Select date range"
                  separator="-"
                  showFooter={true}
                />
              </div>

              {/* Location Filter */}
              <div className="relative min-w-[170px]">
                <ReactSelect
                  name="location"
                  placeholder="Select Location"
                  value={filters.locationIds?.[0] || ""}
                  onChange={(_, value) => {
                    const selectedIds =
                      value && typeof value === "string" ? [value] : [];
                    updateFilter("locationIds", selectedIds);
                  }}
                  options={[
                    { label: "All Locations", value: "" },
                    ...locations,
                  ]}
                  loading={locationsLoading}
                />
              </div>
             {/* Agent Filter */}
              <div className="relative min-w-[170px]">
                <ReactSelect
                  name="agents"
                  placeholder="Select Agent"
                  value={filters.agentIds?.[0] || ""}
                  onChange={(_, value) => {
                    const selectedIds =
                      value && typeof value === "string" ? [value] : [];
                    updateFilter("agentIds", selectedIds);
                  }}
                  options={[{ label: "All Agents", value: "" }, ...agents]}
                  loading={agentsLoading}
                  disabled={
                    !filters.locationIds ||
                    filters.locationIds.length === 0 ||
                    filters.locationIds.every((id) => id === "")
                  }
                />
              </div>

              <button
                onClick={clearFilters}
                className="h-[38px] px-4 text-[#233580] hover:bg-indigo-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                Clear
              </button>
            </div>
          </Card>

          {/* Current Day Statistics */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold text-slate-900">
                Current Day Statistics
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Total Calls</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">
                      {currentDayStats.totalCalls}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {currentDayStats.totalCallsDate}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500">
                    <Phone className="w-6 h-6" />
                  </div>
                </div>
              </Card>
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Avg Duration</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">
                      {currentDayStats.avgDuration}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {currentDayStats.avgDurationSecondary}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-sky-50 flex items-center justify-center text-sky-500">
                    <Clock className="w-6 h-6" />
                  </div>
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Peak Hour</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">
                      {currentDayStats.peakHour}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {currentDayStats.peakHourCalls}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
                    <Lucide icon="TrendingUp" className="w-6 h-6" />
                  </div>
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100 text-wrap">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Active Agents</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1 ">
                      {currentDayStats.activeAgentsCount}
                    </p>
                    <div className="relative group">
                      <p className="text-[11px] text-slate-400 mt-1 truncate text-wrap cursor-help">
                        {getTruncatedActiveAgents(
                          currentDayStats.activeAgentsLabel,
                        )}
                      </p>
                      {currentDayStats.activeAgentsLabel && (
                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-normal max-w-xs z-50 shadow-lg">
                          <div className="break-words">
                            {currentDayStats.activeAgentsLabel}
                          </div>
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-violet-50 flex items-center justify-center text-violet-500">
                    <Lucide icon="Users" className="w-6 h-6" />
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Agent Performance */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold text-slate-900">
                Agent Performance
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100 text-wrap">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Calls Handled</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">
                      {agentPerformanceStats.callsHandled}
                    </p>
                    <div className="relative group">
                      <p className="text-[11px] text-slate-400 mt-1 truncate text-wrap cursor-help">
                        {getTruncatedCallsHandledAgents(
                          agentPerformanceStats.callsHandledLabel,
                        )}
                      </p>
                      {agentPerformanceStats.callsHandledLabel && (
                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-normal max-w-xs z-50 shadow-lg">
                          <div className="break-words">
                            {agentPerformanceStats.callsHandledLabel}
                          </div>
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-500">
                    <Lucide icon="Headphones" className="w-6 h-6" />
                  </div>
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Avg Talk Time</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">
                      {agentPerformanceStats.avgTalkTime}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {agentPerformanceStats.avgTalkTimeLabel}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center text-teal-500">
                    <Clock className="w-6 h-6" />
                  </div>
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">
                      Total Talk Time
                    </p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">
                      {agentPerformanceStats.totalTalkTime  }
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {agentPerformanceStats.totalTalkTimeLabel}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                    <Lucide icon="BarChart3" className="w-6 h-6" />
                  </div>
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Call Length</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-1">
                   {agentPerformanceStats.talkLengthBreakdown}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Normal / Very Short / Long
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-500">
                    <Lucide icon="Activity" className="w-6 h-6" />
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Call Trends */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold text-slate-900">
                Call Trends
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-2">
                  <p className="text-xs font-semibold text-slate-900">
                    Hourly Call Heatmap
                  </p>
                </div>
                <div className="px-4 pb-4 h-44">
                  <canvas ref={hourlyChartRef} className="w-full h-full" />
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-2">
                  <p className="text-xs font-semibold text-slate-900">
                    Daily Volume Trend
                  </p>
                </div>
                <div className="px-4 pb-4 h-44">
                  <canvas ref={dailyChartRef} className="w-full h-full" />
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-visible">
                <div className="px-4 pt-4 pb-2">
                  <p className="text-xs font-semibold text-slate-900">
                    Queue-wise Distribution
                  </p>
                </div>
                <div className="px-4 pb-2 h-44 flex items-center justify-center overflow-visible">
                  <canvas ref={queueChartRef} className="w-full h-full" />
                </div>

                <div className="px-4 pb-4 flex flex-wrap gap-x-4 gap-y-2 text-[10px]">
                  {queueLabels.map((label: string, idx: number) => (
                    <div
                      key={`${label}-${idx}`}
                      className="flex items-center gap-2 relative group cursor-help"
                    >
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{
                          backgroundColor:
                            queueColors[idx % queueColors.length],
                        }}
                      />
                      <span className="text-slate-500 truncate max-w-[120px]">
                        {label}
                      </span>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded-lg py-1.5 px-3 whitespace-nowrap z-50 shadow-lg">
                        {label}: {queueValues[idx]} calls
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>

          {/* Customer Insights */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold text-slate-900">
                Customer Insights
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">
                      Unique Customers
                    </p>
                    <p className="text-xl font-semibold text-slate-900 mt-1">
                      {customerInsights?.unique_customers?.count ?? 0}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-violet-50 flex items-center justify-center text-violet-500">
                    <Lucide icon="Users" className="w-6 h-6" />
                  </div>
                </div>
                <div className="px-4 pb-4 text-xs text-slate-600 space-y-1.5">
                  {uniqueCustomers.map((c: any, idx: number) => (
                    <div key={idx} className="flex justify-between">
                      <span className="truncate">{c?.name ?? "-"}</span>
                      <span className="text-slate-400">
                        {c?.calls ?? 0} calls
                      </span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">
                      High-frequency Numbers
                    </p>
                    <p className="text-xl font-semibold text-slate-900 mt-1">
                      {customerInsights?.high_frequency_numbers?.count ??
                        highFrequencyNumbers.length}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-fuchsia-50 flex items-center justify-center text-fuchsia-500">
                    <Lucide icon="Phone" className="w-6 h-6" />
                  </div>
                </div>
                <div className="px-4 pb-4 text-xs text-slate-600 space-y-1.5">
                  {highFrequencyNumbers.map((n: any, idx: number) => (
                    <div key={idx} className="flex justify-between">
                      <span className="truncate">{n?.phone_number ?? "-"}</span>
                      <span className="text-slate-400">
                        {n?.calls ?? 0} calls
                      </span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-slate-400">Query Summary</p>
                    <p className="text-xl font-semibold text-slate-900 mt-1">
                      {customerInsights?.query_summary?.count ??
                        querySummary.length}
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-violet-50 flex items-center justify-center text-violet-500">
                    <Lucide icon="List" className="w-6 h-6" />
                  </div>
                </div>
                <div className="px-4 pb-4 text-xs text-slate-600 space-y-1.5">
                  {querySummary.map((q: any, idx: number) => (
                    <div key={idx} className="flex justify-between">
                      <span className="truncate">{q?.query_number ?? "-"}</span>
                      <span className="text-slate-400">
                        {q?.calls ?? 0} calls
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>

          {/* IVR & Routing Health */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold text-slate-900">
                IVR & Routing Health
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col">
                <div className="px-4 pt-4 pb-3">
                  <h3 className="text-xs font-semibold text-slate-900">
                    Calls per IVR Menu
                  </h3>
                </div>
                <div className="px-4 pb-4 h-44">
                  <canvas ref={ivrChartRef} className="w-full h-full" />
                </div>
              </Card>

              <Card className="bg-white rounded-2xl shadow-sm border border-slate-100">
                <div className="px-4 pt-4 pb-3">
                  <h3 className="text-xs font-semibold text-slate-900">
                    Most Used IVR Inputs
                  </h3>
                </div>
                <div className="px-4 pb-4 text-xs text-slate-600 space-y-2">
                  {mostUsedIvrInputs.map((item: any, idx: number) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between"
                    >
                      <span className="truncate">{item?.label ?? "-"}</span>
                      <span className="text-slate-400">
                        {item?.call_count ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  getCalls: state.callLogs,
  users: state.users,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchData: (payload?: any) => dispatch(fetchLocationBaseAgents(payload)),
  fetchAllUsers: () => dispatch(fetchAllUsers()),
});

export default connect(mapState, mapDispatchToProps)(CallLogsAnalytics);
